// Colour is for scanning, never for pressure (spec v3 §2). One place owns the
// palette so workspaces, projects, labels and states agree everywhere.

export const HUES = [
  'sky',
  'violet',
  'emerald',
  'amber',
  'rose',
  'teal',
  'orange',
  'lime',
  'fuchsia',
  'slate',
] as const
export type Hue = (typeof HUES)[number]

/** Tailwind-ish tokens per hue: accent (solid), soft (tinted bg), text (on soft), ring. */
const TOKENS: Record<Hue, { accent: string; soft: string; text: string; stripe: string }> = {
  sky: { accent: '#7dd3fc', soft: 'rgba(125,211,252,0.14)', text: '#bae6fd', stripe: '#38bdf8' },
  violet: { accent: '#c4b5fd', soft: 'rgba(196,181,253,0.14)', text: '#ddd6fe', stripe: '#a78bfa' },
  emerald: { accent: '#6ee7b7', soft: 'rgba(110,231,183,0.14)', text: '#a7f3d0', stripe: '#34d399' },
  amber: { accent: '#fcd34d', soft: 'rgba(252,211,77,0.14)', text: '#fde68a', stripe: '#fbbf24' },
  rose: { accent: '#fda4af', soft: 'rgba(253,164,175,0.14)', text: '#fecdd3', stripe: '#fb7185' },
  teal: { accent: '#5eead4', soft: 'rgba(94,234,212,0.14)', text: '#99f6e4', stripe: '#2dd4bf' },
  orange: { accent: '#fdba74', soft: 'rgba(253,186,116,0.14)', text: '#fed7aa', stripe: '#fb923c' },
  lime: { accent: '#bef264', soft: 'rgba(190,242,100,0.14)', text: '#d9f99d', stripe: '#a3e635' },
  fuchsia: { accent: '#f0abfc', soft: 'rgba(240,171,252,0.14)', text: '#f5d0fe', stripe: '#e879f9' },
  slate: { accent: '#cbd5e1', soft: 'rgba(148,163,184,0.14)', text: '#e2e8f0', stripe: '#94a3b8' },
}

export const tokens = (hue: Hue | undefined) => TOKENS[hue && HUES.includes(hue) ? hue : 'sky']

/**
 * A hue as text on a tinted chip, readable on both schemes: the pastel on
 * dark surfaces, a darkened stripe on light ones (a pastel on white is gone).
 * `light-dark()` follows the page's color-scheme, which the Shell sets.
 */
export const tintText = (hue: Hue | undefined) => {
  const t = tokens(hue)
  return `light-dark(color-mix(in oklab, ${t.stripe} 60%, black), ${t.text})`
}

/** CSS variables applied to the shell for the current workspace accent. */
export const accentVars = (hue: Hue | undefined, light = false): Record<string, string> => {
  const t = tokens(hue)
  return {
    '--color-accent': light ? t.stripe : t.accent,
    '--color-accent-soft': t.soft,
    '--color-accent-text': light ? t.stripe : t.text,
    '--color-on-accent': '#0b1220',
  }
}

/** Relative luminance of a hex colour (0 dark … 1 light); undefined for anything else. */
export const luminance = (hex: string): number | undefined => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return undefined
  const n = parseInt(m[1]!, 16)
  const ch = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * ch(n >> 16) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255)
}

/**
 * The ink for text on an accent fill: whichever of white and the dark ink reads
 * better against it (WCAG contrast). White on a light lavender or sky was about
 * 2.5:1; the crossover sits near a luminance of 0.2, not halfway.
 */
export const onAccentFor = (hex: string, dark = '#0b1220'): string | undefined => {
  const lum = luminance(hex)
  const ink = luminance(dark)
  if (lum === undefined || ink === undefined) return undefined
  const onWhite = 1.05 / (lum + 0.05)
  const onDark = (lum + 0.05) / (ink + 0.05)
  return onWhite > onDark ? '#ffffff' : dark
}

/**
 * Accent variables for a workspace: its own accent colour when the palette
 * names one (any CSS colour), else the named hue. Text on the accent flips to
 * light when the accent is dark enough (Latte's saturated accents).
 */
export const accentVarsFor = (
  palette: PaletteRecord | undefined,
  hue: Hue | undefined,
  light = false,
): Record<string, string> => {
  const own = palette?.accent?.trim()
  if (!own) return accentVars(hue, light)
  const onAccent = onAccentFor(own) ?? '#0b1220'
  return {
    '--color-accent': own,
    '--color-accent-soft': `color-mix(in oklab, ${own} 16%, transparent)`,
    '--color-accent-text': light ? own : `color-mix(in oklab, ${own} 80%, white)`,
    '--color-on-accent': onAccent,
  }
}

/** Stable hue for a label value: same on every device, no config needed. */
export const hueFor = (keyValue: string): Hue => {
  let h = 2166136261
  for (let i = 0; i < keyValue.length; i++) {
    h ^= keyValue.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  // skip slate for derived colours; it's reserved for "nothing to say"
  const usable = HUES.filter((x) => x !== 'slate')
  return usable[h % usable.length]!
}

/**
 * The tint of an app's icon on the phone's home screens and launcher. Fixed
 * for the built-in apps so they read the same everywhere (Today warm, Inbox
 * cool); a pinned page or list, or a pack's page, takes its hue from its id.
 */
const APP_HUES: Record<string, Hue> = {
  home: 'slate',
  today: 'amber',
  inbox: 'sky',
  upcoming: 'violet',
  projects: 'teal',
  pages: 'slate',
  lists: 'emerald',
  calendar: 'rose',
  review: 'orange',
  someday: 'sky',
  recurring: 'lime',
  agents: 'fuchsia',
  graph: 'violet',
  log: 'emerald',
  search: 'slate',
  captures: 'teal',
  library: 'orange',
  music: 'rose',
  focus: 'amber',
  wellbeing: 'violet',
  dates: 'orange',
  people: 'rose',
  whatsnew: 'fuchsia',
  desk: 'sky',
  activity: 'slate',
  trash: 'slate',
  settings: 'slate',
}
export const appHue = (id: string): Hue => APP_HUES[id] ?? hueFor(id)

/**
 * An agent's colour by phase in the graph (views/AgentGraph.tsx): working
 * is the accent, your turn is the warm now colour, waiting for room is a
 * hairline, stopped is amber, idle and ended fade.
 */
export const graphPhaseColour = (phase: string | undefined): string => {
  switch (phase) {
    case 'Working':
    case 'Starting':
      return 'var(--color-accent)'
    case 'Waiting':
      return NOW_COLOUR
    case 'Pending':
      return 'var(--color-line-strong)'
    // its own hue: amber sat too close to Waiting's warm now colour
    case 'Failed':
      return TOKENS.rose.stripe
    case 'Suspended':
      return 'var(--color-muted)'
  }
  return 'var(--color-faint)'
}

/**
 * The factory around the agents in the graph (cluster nodes, sandboxes, problem
 * pods, Argo apps and today's spend too, in the muted, amber and green of health): issues in green (Forgejo's open),
 * pages in sky, a build under way in the accent (queued muted and dashed), a ship
 * in the warm now colour (amber when stuck), environments muted, and a card
 * waiting for the owner in the now colour, like an agent whose turn it is yours.
 */
export const graphKindColour = (kind: string, phase?: string): string => {
  switch (kind) {
    case 'issue':
      return TOKENS.emerald.stripe
    case 'doc':
      return TOKENS.sky.stripe
    case 'build':
      return phase === 'queued' ? 'var(--color-muted)' : 'var(--color-accent)'
    case 'ship':
      return phase === 'failed' ? '#f59e0b' : NOW_COLOUR
    case 'env':
      return 'var(--color-muted)'
    case 'card':
      return NOW_COLOUR
    case 'node':
    case 'sandbox':
      // a node or sandbox that is not up is amber, like a build that stopped
      return phase === 'ready' || phase === undefined ? 'var(--color-muted)' : '#f59e0b'
    case 'problem':
      return '#f59e0b'
    case 'app':
      // Argo CD's own words: only Healthy is green
      return phase === 'Healthy'
        ? TOKENS.emerald.stripe
        : phase === 'Progressing'
          ? TOKENS.sky.stripe
          : '#f59e0b'
    case 'spend':
      return 'var(--color-accent)'
  }
  return 'var(--color-faint)'
}

/**
 * Prime's own mark, after its app icon (public/icons/prime.svg): the warm star
 * on dusk indigo. The values live in index.css (`--prime-mark-*`) so black
 * mode can draw it monochrome like everything else there.
 */
export const PRIME_MARK = {
  background: 'var(--prime-mark-bg)',
  color: 'var(--prime-mark-fg)',
  /** the flat disc behind the star where a gradient cannot go (the Graph's node) */
  disc: 'var(--prime-mark-disc)',
} as const

/** A Date's hue by category, so a birthday reads as one at a glance anywhere. */
const DATE_HUES: Record<string, Hue> = {
  birthday: 'fuchsia',
  anniversary: 'rose',
  appointment: 'sky',
  deadline: 'amber',
  bill: 'teal',
  countdown: 'violet',
  other: 'slate',
}
export const dateHue = (category: string): Hue => DATE_HUES[category] ?? 'slate'

/** A person's hue, from their handle, so their avatar is the same everywhere. */
export const personHue = (key: string): Hue => hueFor(`person:${key}`)

/** Surface palettes a workspace can pick (spec v3 §2). Accent comes from `colour`. */
export const THEMES = ['midnight', 'graphite', 'forest', 'dusk', 'ember', 'halloween', 'paper'] as const
export type Theme = (typeof THEMES)[number]

interface Surface {
  ink: string
  panel: string
  raised: string
  line: string
  lineStrong: string
  fg: string
  fg2: string
  muted: string
  faint: string
  light?: boolean
}

export const THEME_SURFACES: Record<Theme, Surface & { label: string; blurb: string }> = {
  midnight: {
    label: 'Midnight',
    blurb: 'deep navy, the default',
    ink: '#0a0f1a',
    panel: '#121a29',
    raised: '#1a2436',
    line: '#243044',
    lineStrong: '#34425a',
    fg: '#f1f5f9',
    fg2: '#cbd5e1',
    muted: '#8b97ac',
    faint: '#5c6a80',
  },
  graphite: {
    label: 'Graphite',
    blurb: 'neutral dark grey',
    ink: '#0f1012',
    panel: '#17181b',
    raised: '#1f2024',
    line: '#2a2b30',
    lineStrong: '#3a3b42',
    fg: '#f4f4f5',
    fg2: '#d4d4d8',
    muted: '#9a9aa3',
    faint: '#66666e',
  },
  forest: {
    label: 'Forest',
    blurb: 'dark green',
    ink: '#0b1410',
    panel: '#10201a',
    raised: '#172b23',
    line: '#1f3a2f',
    lineStrong: '#2c4d3f',
    fg: '#ecfdf5',
    fg2: '#c9e8d6',
    muted: '#8fb3a0',
    faint: '#5f8272',
  },
  dusk: {
    label: 'Dusk',
    blurb: 'warm violet dark',
    ink: '#110e1a',
    panel: '#191428',
    raised: '#221c36',
    line: '#2f2748',
    lineStrong: '#40365f',
    fg: '#f5f3ff',
    fg2: '#d6ccf2',
    muted: '#9f93be',
    faint: '#6c6288',
  },
  ember: {
    label: 'Ember',
    blurb: 'warm brown dark',
    ink: '#140f0c',
    panel: '#1e1612',
    raised: '#29201a',
    line: '#3a2d24',
    lineStrong: '#4f3e32',
    fg: '#fff7ed',
    fg2: '#f1dccb',
    muted: '#b09a86',
    faint: '#7c6a5a',
  },
  halloween: {
    label: 'Halloween',
    blurb: 'near-black purple, pumpkin orange',
    ink: '#0d0911',
    panel: '#161020',
    raised: '#20172d',
    line: '#2f2342',
    lineStrong: '#443360',
    fg: '#f7f0ff',
    fg2: '#dccdf0',
    muted: '#a899c0',
    faint: '#74678c',
  },
  paper: {
    label: 'Paper',
    blurb: 'light, off-white',
    ink: '#f6f5f1',
    panel: '#ffffff',
    raised: '#efede7',
    line: '#e2dfd6',
    lineStrong: '#c9c4b8',
    fg: '#1a1a1a',
    fg2: '#3a3a3a',
    muted: '#6b6b6b',
    faint: '#9a9a9a',
    light: true,
  },
}

/** Sensible accent per theme, used when a workspace hasn't chosen one. */
export const THEME_DEFAULT_ACCENT: Record<Theme, Hue> = {
  midnight: 'sky',
  graphite: 'lime',
  forest: 'emerald',
  dusk: 'violet',
  ember: 'orange',
  halloween: 'orange',
  paper: 'sky',
}

/** CSS variables for a whole surface palette. */
export const themeVars = (theme: string | undefined, palette?: PaletteRecord): Record<string, string> => {
  const t = surfaceFor(theme, palette)
  return {
    '--color-ink': t.ink,
    '--color-panel': t.panel,
    '--color-raised': t.raised,
    '--color-line': t.line,
    '--color-line-strong': t.lineStrong,
    '--color-fg': t.fg,
    '--color-fg-2': t.fg2,
    '--color-muted': t.muted,
    '--color-faint': t.faint,
    'color-scheme': t.light ? 'light' : 'dark',
  }
}

/**
 * A workspace's own palette (theme "custom"): the Surface keys as CSS colours
 * plus `scheme`, optionally `accent` (any CSS colour, replaces the named hue)
 * and `name` (the preset it started from, for the form).
 */
export type PaletteRecord = Record<string, string>
export const CUSTOM_THEME = 'custom'

const surfaceFromRecord = (r: PaletteRecord | undefined): Surface | undefined => {
  if (!r?.ink || !r.panel || !r.fg) return undefined
  return {
    ink: r.ink,
    panel: r.panel,
    raised: r.raised ?? r.panel,
    line: r.line ?? r.raised ?? r.panel,
    lineStrong: r.lineStrong ?? r.line ?? r.panel,
    fg: r.fg,
    fg2: r.fg2 ?? r.fg,
    muted: r.muted ?? r.fg2 ?? r.fg,
    faint: r.faint ?? r.muted ?? r.fg,
    light: r.scheme === 'light',
  }
}

/** The surface for a workspace: a preset, or its own palette when theme is "custom". */
export const surfaceFor = (theme: string | undefined, palette?: PaletteRecord): Surface =>
  (theme === CUSTOM_THEME ? surfaceFromRecord(palette) : undefined) ??
  THEME_SURFACES[theme && THEMES.includes(theme as Theme) ? (theme as Theme) : 'midnight']

export const isLightTheme = (theme: string | undefined, palette?: PaletteRecord) =>
  !!surfaceFor(theme, palette).light

// OKLCH hue angles of the named accents, so a generated palette can pick a fitting one
const HUE_ANGLE: Record<Hue, number> = {
  rose: 10,
  orange: 55,
  amber: 80,
  lime: 125,
  emerald: 160,
  teal: 185,
  sky: 230,
  slate: 260,
  violet: 290,
  fuchsia: 320,
}
/** The named hue nearest a hex colour, by its OKLCH hue angle; slate for greys, undefined for non-hex. */
export const hueOfColour = (css: string): Hue | undefined => {
  const m = /^#([0-9a-f]{6})$/i.exec(css.trim())
  if (!m) return undefined
  const n = parseInt(m[1]!, 16)
  const lin = (v: number) => {
    const c = v / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const r = lin(n >> 16)
  const g = lin((n >> 8) & 255)
  const b = lin(n & 255)
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const mm = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const ss = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const A = 1.9779984951 * l - 2.428592205 * mm + 0.4505937099 * ss
  const B = 0.0259040371 * l + 0.7827717662 * mm - 0.808675766 * ss
  if (Math.hypot(A, B) < 0.045) return 'slate'
  return nearestHue((Math.atan2(B, A) * 180) / Math.PI, true)
}

/** A full editable record for the editor, seeded from what a workspace shows now. */
export const paletteFromSurface = (s: Surface, accent: string): PaletteRecord => ({
  ink: s.ink,
  panel: s.panel,
  raised: s.raised,
  line: s.line,
  lineStrong: s.lineStrong,
  fg: s.fg,
  fg2: s.fg2,
  muted: s.muted,
  faint: s.faint,
  scheme: s.light ? 'light' : 'dark',
  accent,
})

const nearestHue = (angle: number, chromatic = false): Hue => {
  const a = ((angle % 360) + 360) % 360
  let best: Hue = 'sky'
  let d = 999
  for (const [h, deg] of Object.entries(HUE_ANGLE) as [Hue, number][]) {
    if (chromatic && h === 'slate') continue
    const dd = Math.min(Math.abs(a - deg), 360 - Math.abs(a - deg))
    if (dd < d) {
      d = dd
      best = h
    }
  }
  return best
}

/**
 * A fresh surface palette: one base hue tinting every layer, lightness stepped
 * so contrast stays readable, and an accent that sits well with it. Dark four
 * times out of five. Colours are OKLCH strings, which the theme vars accept as-is.
 */
export const randomPalette = (): { palette: PaletteRecord; accent: Hue } => {
  const h = Math.floor(Math.random() * 360)
  const light = Math.random() < 0.2
  const c = light ? 0.008 + Math.random() * 0.02 : 0.012 + Math.random() * 0.04
  const at = (l: number, chroma = c) => `oklch(${l.toFixed(3)} ${chroma.toFixed(3)} ${h})`
  const base = light ? 0 : Math.random() * 0.05
  const palette: PaletteRecord = light
    ? {
        ink: at(0.975),
        panel: at(0.995, c / 2),
        raised: at(0.94),
        line: at(0.89),
        lineStrong: at(0.8),
        fg: at(0.2, c / 2),
        fg2: at(0.32, c / 2),
        muted: at(0.5, c / 2),
        faint: at(0.66, c / 2),
        scheme: 'light',
      }
    : {
        ink: at(0.15 + base),
        panel: at(0.2 + base),
        raised: at(0.25 + base),
        line: at(0.31 + base),
        lineStrong: at(0.41 + base),
        fg: at(0.96, c / 3),
        fg2: at(0.86, c / 3),
        muted: at(0.68, c / 2),
        faint: at(0.5, c / 2),
        scheme: 'dark',
      }
  const offsets = [0, 30, 150, 180, 210]
  const accent = nearestHue(h + offsets[Math.floor(Math.random() * offsets.length)]!)
  return { palette, accent }
}

export type Tone =
  'muted' | 'accent' | 'warn' | 'waiting' | 'someday' | 'done' | 'running' | 'p1' | 'p2' | 'p3'

/** State → tone (spec v3 §2). */
export const stateTone = (state: string, opts: { slipped?: boolean; running?: boolean } = {}): Tone => {
  if (opts.running) return 'running'
  if (opts.slipped) return 'warn'
  switch (state) {
    case 'waiting':
      return 'waiting'
    case 'someday':
      return 'someday'
    case 'done':
      return 'done'
    default:
      return 'muted'
  }
}

export const priorityTone = (p: number): Tone => (p >= 3 ? 'p3' : p === 2 ? 'p2' : p === 1 ? 'p1' : 'muted')

/** Class names for a Tag by tone. Kept here so views never pick colours themselves. */
export const toneClass: Record<Tone, string> = {
  muted: 'bg-raised text-muted',
  accent: 'bg-accent-soft text-accent-text',
  warn: 'bg-amber-500/15 text-amber-500',
  waiting: 'bg-violet-500/15 text-violet-400',
  someday: 'bg-raised text-fg-2',
  done: 'bg-emerald-500/15 text-emerald-500',
  running: 'bg-accent-soft text-accent-text',
  p1: 'bg-amber-500/15 text-amber-500',
  p2: 'bg-orange-500/15 text-orange-500',
  p3: 'bg-rose-500/15 text-rose-500',
}

/**
 * A read that failed (`Failed` in components/ui.tsx): the mark beside the
 * sentence. Amber, never red: nothing is lost, the page is only waiting.
 */
export const failedMark = 'text-amber-500'

/** Whether a piece of infrastructure is up: well, needs a look, or neither (finished, unknown, soft no). */
export type Health = 'ok' | 'warn' | 'off'

/** Dot classes by health (Releases' runners and registry, Settings → Security). */
export const healthDot: Record<Health, string> = {
  ok: 'bg-emerald-400',
  warn: 'bg-amber-400',
  off: 'bg-line-strong',
}

/**
 * An alert's severity dot (the Infrastructure page): critical in the danger
 * hue, a warning amber like everything that needs a look, info quiet.
 */
export const alertDot: Record<'critical' | 'warning' | 'info' | 'none', string> = {
  critical: 'bg-rose-400',
  warning: 'bg-amber-400',
  info: 'bg-sky-400',
  none: 'bg-line-strong',
}

/**
 * A disk's use bar (the Infrastructure page's Storage): the accent while there
 * is room, amber past 85 % like everything that needs a look, the danger hue
 * past 95 % (the lines internal/storage warns at; `fillOf` in lib/storage.ts).
 */
export const useBar: Record<'track' | 'ok' | 'warn' | 'critical', string> = {
  track: 'bg-raised',
  ok: 'bg-accent',
  warn: 'bg-amber-400',
  critical: 'bg-rose-400',
}

/**
 * The infra pack's status (its Overview's estate cards, a host on Hosts;
 * lib/infra.ts, lib/hosts.ts): green while all is well, amber when something
 * needs a look, the danger hue when something is down or failing, quiet when
 * nothing has been read. `statusText` is a figure past its line (a host's
 * memory at 92%), in the same hues.
 */
export const statusDot: Record<'ok' | 'warn' | 'critical' | 'unknown', string> = {
  ok: 'bg-emerald-400',
  warn: 'bg-amber-400',
  critical: 'bg-rose-400',
  unknown: 'bg-line-strong',
}
export const statusText: Record<'ok' | 'warn' | 'critical', string> = {
  ok: 'text-fg',
  warn: 'text-amber-500',
  critical: 'text-rose-500',
}

/**
 * The Pipeline's marks (components/Pipeline.tsx): a row's dot by where it is
 * (moving pulses in the now colour, stuck is amber like a failed read: it is
 * waiting, nothing is lost; live is done), and a stuck row's reason.
 */
export const flowMark = {
  moving: 'bg-[var(--color-now)]',
  waiting: 'bg-fg/30',
  stuck: 'bg-amber-400',
  live: 'bg-emerald-400',
  stuckText: 'text-amber-500',
} as const

/** Style for a label tag: override from settings wins, else the hashed hue. */
export const labelStyle = (
  key: string,
  value: string,
  overrides: Record<string, { colour?: string; icon?: string }> = {},
) => {
  const o = overrides[`${key}:${value}`]
  const hue =
    (o?.colour as Hue | undefined) && HUES.includes(o!.colour as Hue)
      ? (o!.colour as Hue)
      : hueFor(`${key}:${value}`)
  const t = tokens(hue)
  // light-dark() follows the color-scheme the shell sets on <html>, so chips stay readable on the Paper palette
  return { hue, icon: o?.icon, style: { background: t.soft, color: tintText(hue) } }
}

/** A leading emoji (or other pictograph) at the start of a title. */
const LEADING_EMOJI = /^(\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic})*)\s+(.+)$/u
export const splitLeadingEmoji = (title: string): { icon?: string; title: string } => {
  const m = LEADING_EMOJI.exec(title.trim())
  return m ? { icon: m[1], title: m[2]!.trim() } : { title: title.trim() }
}

// ---- presets that fill a workspace's own palette -----------------------------

export interface PalettePreset {
  id: string
  label: string
  blurb: string
  palette: PaletteRecord
  /** named accents that suit this surface, in the order they are offered */
  accents: { name: string; hex: string }[]
}

const catppuccin = (
  id: string,
  label: string,
  blurb: string,
  c: Record<string, string>,
  scheme: 'dark' | 'light',
): PalettePreset => ({
  id,
  label,
  blurb,
  palette:
    scheme === 'dark'
      ? {
          name: id,
          ink: c.crust!,
          panel: c.base!,
          raised: c.surface0!,
          line: c.surface1!,
          lineStrong: c.surface2!,
          fg: c.text!,
          fg2: c.subtext1!,
          muted: c.subtext0!,
          faint: c.overlay1!,
          scheme: 'dark',
          accent: c.mauve!,
        }
      : {
          name: id,
          ink: c.base!,
          panel: c.mantle!,
          raised: c.crust!,
          line: c.surface0!,
          lineStrong: c.surface1!,
          fg: c.text!,
          fg2: c.subtext1!,
          muted: c.subtext0!,
          faint: c.overlay1!,
          scheme: 'light',
          accent: c.mauve!,
        },
  accents: (
    [
      'rosewater',
      'flamingo',
      'pink',
      'mauve',
      'red',
      'maroon',
      'peach',
      'yellow',
      'green',
      'teal',
      'sky',
      'sapphire',
      'blue',
      'lavender',
    ] as const
  ).map((name) => ({ name, hex: c[name]! })),
})

/** Catppuccin (https://catppuccin.com), all four flavours, mapped onto the app's layers. */
export const PRESETS: PalettePreset[] = [
  catppuccin(
    'catppuccin-mocha',
    'Catppuccin Mocha',
    'the dark one',
    {
      crust: '#11111b',
      mantle: '#181825',
      base: '#1e1e2e',
      surface0: '#313244',
      surface1: '#45475a',
      surface2: '#585b70',
      text: '#cdd6f4',
      subtext1: '#bac2de',
      subtext0: '#a6adc8',
      overlay1: '#7f849c',
      rosewater: '#f5e0dc',
      flamingo: '#f2cdcd',
      pink: '#f5c2e7',
      mauve: '#cba6f7',
      red: '#f38ba8',
      maroon: '#eba0ac',
      peach: '#fab387',
      yellow: '#f9e2af',
      green: '#a6e3a1',
      teal: '#94e2d5',
      sky: '#89dceb',
      sapphire: '#74c7ec',
      blue: '#89b4fa',
      lavender: '#b4befe',
    },
    'dark',
  ),
  catppuccin(
    'catppuccin-macchiato',
    'Catppuccin Macchiato',
    'a shade softer',
    {
      crust: '#181926',
      mantle: '#1e2030',
      base: '#24273a',
      surface0: '#363a4f',
      surface1: '#494d64',
      surface2: '#5b6078',
      text: '#cad3f5',
      subtext1: '#b8c0e0',
      subtext0: '#a5adcb',
      overlay1: '#8087a2',
      rosewater: '#f4dbd6',
      flamingo: '#f0c6c6',
      pink: '#f5bde6',
      mauve: '#c6a0f6',
      red: '#ed8796',
      maroon: '#ee99a0',
      peach: '#f5a97f',
      yellow: '#eed49f',
      green: '#a6da95',
      teal: '#8bd5ca',
      sky: '#91d7e3',
      sapphire: '#7dc4e4',
      blue: '#8aadf4',
      lavender: '#b7bdf8',
    },
    'dark',
  ),
  catppuccin(
    'catppuccin-frappe',
    'Catppuccin Frappé',
    'the gentle dark',
    {
      crust: '#232634',
      mantle: '#292c3c',
      base: '#303446',
      surface0: '#414559',
      surface1: '#51576d',
      surface2: '#626880',
      text: '#c6d0f5',
      subtext1: '#b5bfe2',
      subtext0: '#a5adce',
      overlay1: '#838ba7',
      rosewater: '#f2d5cf',
      flamingo: '#eebebe',
      pink: '#f4b8e4',
      mauve: '#ca9ee6',
      red: '#e78284',
      maroon: '#ea999c',
      peach: '#ef9f76',
      yellow: '#e5c890',
      green: '#a6d189',
      teal: '#81c8be',
      sky: '#99d1db',
      sapphire: '#85c1dc',
      blue: '#8caaee',
      lavender: '#babbf1',
    },
    'dark',
  ),
  catppuccin(
    'catppuccin-latte',
    'Catppuccin Latte',
    'the light one',
    {
      crust: '#dce0e8',
      mantle: '#e6e9ef',
      base: '#eff1f5',
      surface0: '#ccd0da',
      surface1: '#bcc0cc',
      surface2: '#acb0be',
      text: '#4c4f69',
      subtext1: '#5c5f77',
      subtext0: '#6c6f85',
      overlay1: '#8c8fa1',
      rosewater: '#dc8a78',
      flamingo: '#dd7878',
      pink: '#ea76cb',
      mauve: '#8839ef',
      red: '#d20f39',
      maroon: '#e64553',
      peach: '#fe640b',
      yellow: '#df8e1d',
      green: '#40a02b',
      teal: '#179299',
      sky: '#04a5e5',
      sapphire: '#209fb5',
      blue: '#1e66f5',
      lavender: '#7287fd',
    },
    'light',
  ),
]

export const presetFor = (palette: PaletteRecord | undefined): PalettePreset | undefined =>
  palette?.name ? PRESETS.find((p) => p.id === palette.name) : undefined

/** The editable surface keys, in the order the editor shows them. */
export const PALETTE_KEYS = [
  ['ink', 'Background'],
  ['panel', 'Cards'],
  ['raised', 'Raised'],
  ['line', 'Lines'],
  ['lineStrong', 'Strong lines'],
  ['fg', 'Text'],
  ['fg2', 'Secondary text'],
  ['muted', 'Muted text'],
  ['faint', 'Faint text'],
  ['accent', 'Accent'],
] as const

// ---- Horizon: the app's own surfaces (lib/horizon.ts picks which) -------------

/**
 * The three surface sets of the Horizon design. Night is a real colour (a deep
 * blue-violet sky, not a tinted black); day is a cool daylight paper; black is
 * true black with hairline greys and no colour of its own, for when the head
 * is busy (after Payload CMS's admin). The workspace keeps its accent in day
 * and night; black is monochrome.
 */
export const HORIZON_SURFACES: Record<'day' | 'night' | 'black' | 'glass', Surface> = {
  night: {
    ink: '#15142b',
    panel: '#1f1d3a',
    raised: '#2a2850',
    line: '#302e55',
    lineStrong: '#433f73',
    fg: '#eceaf7',
    fg2: '#c9c6e3',
    muted: '#9c98c0',
    faint: '#6e6a93',
  },
  day: {
    ink: '#eef2f8',
    panel: '#ffffff',
    raised: '#e3e9f3',
    line: '#d6dde9',
    lineStrong: '#b9c3d4',
    fg: '#15172b',
    fg2: '#33374f',
    muted: '#5b6180',
    faint: '#8a90a9',
    light: true,
  },
  black: {
    ink: '#000000',
    panel: '#0a0a0a',
    raised: '#141414',
    line: '#1f1f1f',
    lineStrong: '#2e2e2e',
    fg: '#f5f5f5',
    fg2: '#d4d4d4',
    muted: '#8f8f8f',
    faint: '#5e5e5e',
  },
  // glass: black's base; the panel tokens stay opaque (inputs and text on them
  // never depend on what is behind), the translucency is applied by the
  // `data-appearance='glass'` block in index.css from GLASS below. Muted is a
  // step lighter than black's so it keeps AA on a blurred panel.
  glass: {
    ink: '#000000',
    panel: '#0a0a0a',
    raised: '#141414',
    line: '#1f1f1f',
    lineStrong: '#2e2e2e',
    fg: '#f5f5f5',
    fg2: '#d4d4d4',
    muted: '#a0a0a0',
    faint: '#6a6a6a',
  },
}

/**
 * Glass's numbers. The CSS reads them as variables (horizonVars) so the rules
 * live in one block and the contrast test checks the very values used.
 * `panelAlpha` is how much of the panel colour covers what is behind it;
 * `worstBehind` the brightest the blurred content behind a panel is expected
 * to be (a screen of white text, blurred, averages well under this).
 */
export const GLASS = {
  panelAlpha: 0.66,
  barAlpha: 0.72,
  // the default glow was 0.14, which a phone screen hardly showed
  glow: 0.3,
  // the most a person can ask for: the contrast test checks text at this strength
  glowMax: 0.4,
  blur: 22,
  worstBehind: '#5a5a5a',
} as const

/** The one warm colour of the design: now, on the horizon. */
export const NOW_COLOUR = '#f2a65a'

/**
 * Danger (revoke, disconnect, reset): full-strength text on its own faint tint,
 * so it never reads as a disabled control. Darker on a light surface.
 */
export const DANGER = { light: '#be123c', dark: '#fda4af' } as const

/**
 * State colours beyond danger (themes studio): defaults per scheme, overridable per theme
 * (`--color-ok|warn|info`; danger keeps `--color-danger`). The studio's contrast check reads them.
 */
export const STATE_COLOURS = {
  dark: { ok: '#86efac', warn: '#fcd34d', info: '#7dd3fc', danger: DANGER.dark },
  light: { ok: '#15803d', warn: '#b45309', info: '#0369a1', danger: DANGER.light },
} as const

/** `#rrggbb` or `#rrggbbaa` to [r, g, b] (0..255), alpha ignored; undefined for anything else. */
export const rgbOfHex = (hex: string): [number, number, number] | undefined => {
  const m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex.trim())
  if (!m) return undefined
  const n = parseInt(m[1]!, 16)
  return [n >> 16, (n >> 8) & 255, n & 255]
}

/** `a` mixed toward `b` by t (0..1) in sRGB, as `#rrggbb`; `a` unchanged when either is not hex. */
export const mixHex = (a: string, b: string, t: number): string => {
  const x = rgbOfHex(a)
  const y = rgbOfHex(b)
  if (!x || !y) return a
  const h = (i: number) =>
    Math.round(x[i]! + (y[i]! - x[i]!) * t)
      .toString(16)
      .padStart(2, '0')
  return `#${h(0)}${h(1)}${h(2)}`
}

const surfaceVars = (t: Surface): Record<string, string> => ({
  '--color-ink': t.ink,
  '--color-panel': t.panel,
  '--color-raised': t.raised,
  '--color-line': t.line,
  '--color-line-strong': t.lineStrong,
  '--color-fg': t.fg,
  '--color-fg-2': t.fg2,
  '--color-muted': t.muted,
  '--color-faint': t.faint,
  '--color-danger': t.light ? DANGER.light : DANGER.dark,
  'color-scheme': t.light ? 'light' : 'dark',
})

/** CSS variables for a Horizon appearance, keeping the workspace's accent where it has colour. */
export const horizonVars = (
  mode: 'day' | 'night' | 'black' | 'glass',
  palette: PaletteRecord | undefined,
  hue: Hue | undefined,
  glass?: { tint: 'suite' | Hue; glow: number; blur: number },
): Record<string, string> => {
  const surface = surfaceVars(HORIZON_SURFACES[mode])
  if (mode === 'black')
    return {
      ...surface,
      '--color-accent': '#f5f5f5',
      '--color-accent-soft': 'rgba(255, 255, 255, 0.08)',
      '--color-accent-text': '#ffffff',
      '--color-on-accent': '#000000',
    }
  if (mode === 'glass')
    return {
      ...surface,
      // state and controls are monochrome as in black (D6); only the ambient glow keeps the suite's hue
      '--color-accent': '#f5f5f5',
      '--color-accent-soft': 'rgba(255, 255, 255, 0.1)',
      '--color-accent-text': '#ffffff',
      '--color-on-accent': '#000000',
      '--glass-glow':
        glass && glass.tint !== 'suite'
          ? tokens(glass.tint).accent
          : accentVarsFor(palette, hue, false)['--color-accent']!,
      '--glass-panel-alpha': String(GLASS.panelAlpha),
      '--glass-bar-alpha': String(GLASS.barAlpha),
      '--glass-glow-strength': `${Math.round((glass?.glow ?? GLASS.glow) * 100)}%`,
      '--glass-blur': `${glass?.blur ?? GLASS.blur}px`,
    }
  const accent = accentVarsFor(palette, hue, mode === 'day')
  if (mode === 'day') {
    // a pale accent on white reads as nothing: text in the accent is darkened, fills keep it
    const a = accent['--color-accent']!
    accent['--color-accent-text'] = `color-mix(in oklab, ${a} 58%, black)`
    accent['--color-on-accent'] = onAccentFor(a, '#15172b') ?? '#15172b'
  }
  return { ...surface, ...accent }
}

/**
 * Code and diffs (components/Code.tsx, components/DiffFile.tsx): the editor palette,
 * after a dark VS Code theme: purple keywords, orange-pink parameters, yellow classes,
 * green strings. Two sets, because the same colour cannot read on white and on night;
 * black and glass share the dark one. Each colour is checked (syntax.test.ts) against
 * the panel mixed with the added and removed row tints, in every appearance.
 */
export const SYNTAX = {
  dark: {
    kw: '#cfa1ed',
    str: '#a5e075',
    num: '#f89b7f',
    com: '#acb2ca',
    type: '#ffcb6b',
    typeParam: '#ffd98e',
    fn: '#8fb3ff',
    param: '#ff9eb5',
    prop: '#8fd8e8',
    cnst: '#f89b7f',
    op: '#89ddff',
    tag: '#ff93ae',
    attr: '#ffcb6b',
    meta: '#c3a6ff',
    rx: '#f4a77a',
    // brackets by depth (gold, orchid, blue), then a closer with no opener
    b0: '#ffd700',
    b1: '#e697e6',
    b2: '#61baff',
    bx: '#ff9494',
    add: '#34d399',
    del: '#fb7185',
    tint: '17%',
    tintStrong: '28%',
    guideAlpha: '9%',
    guideLine: '22%',
    // the rainbow of indent levels
    guides: ['#ffd700', '#7ee787', '#56d4dd', '#4db2ff', '#c792ea', '#ff8aa8'],
  },
  light: {
    kw: '#7524c9',
    str: '#16671d',
    num: '#9c380a',
    com: '#51596a',
    type: '#814e00',
    typeParam: '#7a4a00',
    fn: '#1a4fc4',
    param: '#a62157',
    prop: '#0a6271',
    cnst: '#9c380a',
    op: '#0a6271',
    tag: '#ab1e49',
    attr: '#814e00',
    meta: '#5b36b8',
    rx: '#993c0a',
    b0: '#705600',
    b1: '#982198',
    b2: '#0a59a7',
    bx: '#b01419',
    add: '#16a34a',
    del: '#e11d48',
    tint: '13%',
    tintStrong: '22%',
    guideAlpha: '9%',
    guideLine: '26%',
    guides: ['#b08900', '#2f9e44', '#0e8f9a', '#1c6fd1', '#8e44c8', '#d6336c'],
  },
} as const

/** The CSS variables the code styles read (index.css `.code`); set on the code's own root. */
export const syntaxVars = (light: boolean): Record<string, string> => {
  const s = light ? SYNTAX.light : SYNTAX.dark
  const v: Record<string, string> = {}
  for (const [k, val] of Object.entries(s)) {
    if (typeof val === 'string') v[`--syn-${k}`] = val
  }
  s.guides.forEach((c, i) => {
    v[`--ig-${i}`] = c
  })
  return v
}

/** A learn card's flash when it is answered: a soft wash, not a signal that anything is wrong. */
export const deckFlash = { got: 'bg-emerald-500/20', again: 'bg-amber-500/15' } as const

/** The dots along the top of a deck: done, here, still ahead. */
/** A test's dots by how a question went: no red, to revisit is only the quietest. */
export const testDot = {
  right: 'bg-accent',
  partial: 'bg-accent/45',
  wrong: 'bg-line-strong',
  skipped: 'bg-line-strong',
} as const

export const deckDot = { done: 'bg-accent', here: 'bg-fg', ahead: 'bg-line-strong' } as const

/**
 * The four tiles of a quick pick or a spot card, each its own colour and shape so they are told apart
 * at a glance (and without colour): triangle, diamond, circle, square.
 */
export const deckTiles = [
  { shape: 'triangle', look: 'bg-rose-500/20 text-rose-100 border-rose-400/40' },
  { shape: 'diamond', look: 'bg-sky-500/20 text-sky-100 border-sky-400/40' },
  { shape: 'circle', look: 'bg-amber-500/20 text-amber-100 border-amber-400/40' },
  { shape: 'square', look: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/40' },
  { shape: 'triangle', look: 'bg-violet-500/20 text-violet-100 border-violet-400/40' },
  { shape: 'diamond', look: 'bg-slate-500/25 text-slate-100 border-slate-400/40' },
] as const

/** After an answer a tile is the answer, a pick that was not, or neither; the picked ones are marked. */
export const deckTileAfter = {
  answer: 'ring-2 ring-emerald-400',
  missed: 'opacity-35',
  picked: 'ring-2 ring-fg-2',
} as const

/** The soft countdown bar under a quick pick: it never turns an alarm colour. */
export const deckTimer = 'bg-accent/60'
